/**
 * Analytics Service
 *
 * Core analytics service for Firebase Analytics integration.
 * Provides methods for logging events, setting user properties,
 * and controlling analytics collection.
 *
 * Based on patterns from myAir web application (AngularFire) adapted for React.
 *
 * ## Architecture Overview
 *
 * This service uses a singleton pattern and provides:
 * 1. **Dedicated Event Methods**: Type-safe methods for common events (logLogin, logStudyCreated, etc.)
 * 2. **Generic logEvent Method**: Flexible method for custom events using constants from events.ts
 * 3. **Screen Tracking**: Automatic page view tracking via logScreenView
 * 4. **User Management**: setUserId and setUserProperties for user segmentation
 * 5. **Privacy Controls**: setAnalyticsMode for opt-in/opt-out functionality
 *
 * ## Usage in Components
 *
 * ### Via useAnalytics Hook (Recommended)
 * ```tsx
 * import { useAnalytics } from 'services/analytics';
 *
 * function MyComponent() {
 *   const { logLogin, logError, logStudyCreated } = useAnalytics();
 *
 *   const handleLogin = async () => {
 *     try {
 *       await loginAPI();
 *       logLogin(LoginType.PASSWORD); // Dedicated method - type safe!
 *     } catch (error) {
 *       logError('login_failed', error.message);
 *     }
 *   };
 * }
 * ```
 *
 * ### Direct Service Access (Advanced)
 * ```tsx
 * import { analyticsService, ANALYTICS_EVENTS, ANALYTICS_PARAMS } from 'services/analytics';
 *
 * // For custom events not covered by dedicated methods
 * analyticsService.logEvent(ANALYTICS_EVENTS.FEATURE_USED, {
 *   [ANALYTICS_PARAMS.FEATURE]: 'custom_feature',
 * });
 * ```
 *
 * ## Event Constants Integration
 *
 * All event names and parameters are defined in events.ts and imported here:
 * - ANALYTICS_EVENTS: 60+ event name constants
 * - ANALYTICS_PARAMS: 25+ parameter name constants
 * - Benefits: Type safety, autocomplete, centralized management, easier refactoring
 *
 * ## Environment Separation
 *
 * Analytics data is automatically separated by environment using distinct Measurement IDs:
 * - Dev: G-338842T496
 * - QA: G-E057EP61HK
 * - Prod: G-4NQ091PBP7
 * - Configured via Vite environment variables in .env.dev, .env.qa, .env.prod
 *
 * @see {@link events.ts} for all available event and parameter constants
 * @see {@link useAnalytics} for React hook interface
 * @see {@link useScreenTracking} for automatic screen view tracking
 */

import {
  Analytics,
  logEvent as firebaseLogEvent,
  setUserId as firebaseSetUserId,
  setUserProperties as firebaseSetUserProperties,
  setAnalyticsCollectionEnabled,
} from 'firebase/analytics';

import { getFirebaseAnalytics } from 'configs/firebase';

import { ANALYTICS_EVENTS, ANALYTICS_PARAMS } from './events';
import {
  AnalyticsFeature,
  AnalyticsMode,
  AnalyticsParams,
  LoginType,
  LogoutCause,
  ScreenViewEvent,
  UserProperties,
} from './types';

class AnalyticsService {
  private currentMode: AnalyticsMode = AnalyticsMode.ANONYMOUS;
  private isInitialized = false;

  /**
   * Initialize the analytics service.
   * Should be called after Firebase is initialized.
   */
  initialize(): void {
    const analytics = this.getAnalytics();
    if (analytics) {
      this.isInitialized = true;
      console.log('Analytics service initialized');
    }
  }

  /**
   * Get the Firebase Analytics instance.
   */
  private getAnalytics(): Analytics | null {
    return getFirebaseAnalytics();
  }

  /**
   * Check if analytics is available and enabled.
   */
  private canLog(): boolean {
    return this.isInitialized && this.currentMode !== AnalyticsMode.UNTRACKED;
  }

  /**
   * Set the analytics collection mode.
   */
  async setAnalyticsMode(mode: AnalyticsMode): Promise<void> {
    this.currentMode = mode;
    const analytics = this.getAnalytics();

    if (analytics) {
      const enabled = mode !== AnalyticsMode.UNTRACKED;
      setAnalyticsCollectionEnabled(analytics, enabled);
      console.log(`Analytics collection ${enabled ? 'enabled' : 'disabled'} (mode: ${mode})`);
    }
  }

  /**
   * Get the current analytics mode.
   */
  getAnalyticsMode(): AnalyticsMode {
    return this.currentMode;
  }

  /**
   * Set the user ID for analytics tracking.
   * Pass null to clear the user ID (for anonymous tracking).
   */
  setUserId(userId: string | null): void {
    const analytics = this.getAnalytics();
    if (analytics) {
      firebaseSetUserId(analytics, userId);
      console.log(`Analytics user ID ${userId ? 'set' : 'cleared'}`);
    }
  }

  /**
   * Set user properties for analytics segmentation.
   */
  setUserProperties(properties: UserProperties): void {
    const analytics = this.getAnalytics();
    if (analytics) {
      // Convert properties to the format expected by Firebase
      const firebaseProperties: Record<string, string> = {};

      if (properties.userRole) {
        firebaseProperties[ANALYTICS_PARAMS.USER_ROLE] = properties.userRole;
      }
      // TODO: verify with Karl- innitially used center_count, now changed to user_count
      if (properties.centerCount !== undefined) {
        firebaseProperties[ANALYTICS_PARAMS.USER_COUNT] = String(properties.centerCount);
      }
      if (properties.preferredLanguage) {
        firebaseProperties.preferred_language = properties.preferredLanguage;
      }
      if (properties.userRegion) {
        firebaseProperties.user_region = properties.userRegion;
      }
      if (properties.appVersion) {
        firebaseProperties.app_version = properties.appVersion;
      }

      firebaseSetUserProperties(analytics, firebaseProperties);
    }
  }

  /**
   * Log a custom event.
   */
  logEvent(eventName: string, params?: AnalyticsParams): void {
    if (!this.canLog()) {
      return;
    }

    const analytics = this.getAnalytics();
    if (analytics) {
      // Filter out null/undefined values
      const cleanParams = params
        ? Object.fromEntries(
            Object.entries(params).filter(([, value]) => value !== null && value !== undefined)
          )
        : undefined;

      firebaseLogEvent(analytics, eventName, cleanParams);
    }
  }

  /**
   * Log a screen view event.
   * Used for tracking navigation and screen visits.
   */
  logScreenView(screenView: ScreenViewEvent): void {
    if (!this.canLog()) {
      return;
    }

    const analytics = this.getAnalytics();
    if (analytics) {
      const params: AnalyticsParams = {
        [ANALYTICS_PARAMS.SCREEN_NAME]: screenView.screenName,
        [ANALYTICS_PARAMS.SCREEN_CLASS]: screenView.screenClass || screenView.screenName,
        ...screenView.params,
      };

      // Use custom event name for screen views as Firebase has strict typing for 'screen_view'
      firebaseLogEvent(analytics, ANALYTICS_EVENTS.SCREEN_VIEW_EVENT, params);
    }
  }

  /**
   * Log a web-specific screen view (matches myAir pattern).
   */
  // logScreenViewWeb(screenName: string): void {
  //   this.logEvent(ANALYTICS_EVENTS.SCREEN_VIEW_WEB, {
  //     [ANALYTICS_PARAMS.SCREEN_NAME_WEB]: screenName,
  //   });
  // }

  // ============================================================
  // Authentication Events
  // ============================================================

  /**
   * Log a login event.
   */
  logLogin(loginType: LoginType = LoginType.PASSWORD, region?: string, service?: string): void {
    const params: AnalyticsParams = { [ANALYTICS_PARAMS.LOGIN_TYPE]: loginType };

    if (region) {
      params[ANALYTICS_PARAMS.REGION] = region;
    }
    if (service) {
      params[ANALYTICS_PARAMS.SERVICE] = service;
    }

    // Main login event
    this.logEvent(ANALYTICS_EVENTS.LOGGED_IN, params);

    // Dimension events for analytics segmentation
    if (region) {
      const regionEventName = `${ANALYTICS_EVENTS.LOGIN_BY_REGION}_${region.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
      this.logEvent(regionEventName, {
        [ANALYTICS_PARAMS.LOGIN_TYPE]: loginType,
        [ANALYTICS_PARAMS.REGION]: region,
        [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.AUTHENTICATION,
      });
    }

    if (service) {
      const serviceEventName = `${ANALYTICS_EVENTS.LOGIN_BY_SERVICE}_${service.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
      this.logEvent(serviceEventName, {
        [ANALYTICS_PARAMS.LOGIN_TYPE]: loginType,
        [ANALYTICS_PARAMS.SERVICE]: service,
        [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.AUTHENTICATION,
      });
    }
  }

  /**
   * Log a logout event.
   */
  logLogout(cause: LogoutCause = LogoutCause.USER_INITIATED): void {
    this.logEvent(ANALYTICS_EVENTS.LOGGED_OUT, { [ANALYTICS_PARAMS.LOGOUT_CAUSE]: cause });
  }

  /**
   * Log a password reset request.
   */
  logForgotPassword(): void {
    this.logEvent(ANALYTICS_EVENTS.FORGOT_PASSWORD, {
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.AUTHENTICATION,
    });
  }

  /**
   * Log a two-factor authentication event.
   */
  logTwoFactorAuth(success: boolean): void {
    this.logEvent(ANALYTICS_EVENTS.TWO_FACTOR_AUTH, {
      [ANALYTICS_PARAMS.SUCCESS]: success,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.AUTHENTICATION,
    });
  }

  // ============================================================
  // Study Events
  // ============================================================

  /**
   * Log when a new study is created.
   */
  logStudyCreated(centerId?: string): void {
    this.logEvent(ANALYTICS_EVENTS.STUDY_CREATED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.STUDIES,
    });
  }

  /**
   * Log when a study is viewed.
   */
  logStudyViewed(studyId: string): void {
    this.logEvent(ANALYTICS_EVENTS.STUDY_VIEWED, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.STUDIES,
    });
  }

  /**
   * Log when study settings are updated.
   */
  logStudySettingsUpdated(studyId: string): void {
    this.logEvent(ANALYTICS_EVENTS.STUDY_SETTINGS_UPDATED, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.STUDIES,
    });
  }

  /**
   * Log when a report is generated.
   */
  logReportGenerated(reportType: string, studyId?: string): void {
    this.logEvent(ANALYTICS_EVENTS.REPORT_GENERATED, {
      [ANALYTICS_PARAMS.REPORT_TYPE]: reportType,
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.REPORTS,
    });
  }

  /**
   * Log when a report is downloaded.
   */
  logReportDownloaded(reportType: string, studyId?: string): void {
    this.logEvent(ANALYTICS_EVENTS.REPORT_DOWNLOADED, {
      [ANALYTICS_PARAMS.REPORT_TYPE]: reportType,
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.REPORTS,
    });
  }

  // ============================================================
  // Center Events
  // ============================================================

  /**
   * Log when a center is created.
   */
  logCenterCreated(): void {
    this.logEvent(ANALYTICS_EVENTS.CENTER_CREATED, {
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  /**
   * Log when center settings are updated.
   */
  logCenterSettingsUpdated(centerId: string): void {
    this.logEvent(ANALYTICS_EVENTS.CENTER_SETTINGS_UPDATED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  /**
   * Log when a user is added to a center.
   */
  logCenterUserAdded(centerId: string, userRole: string): void {
    this.logEvent(ANALYTICS_EVENTS.CENTER_USER_ADDED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.USER_ROLE]: userRole,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  // ============================================================
  // Profile Events
  // ============================================================

  /**
   * Log when profile settings are updated.
   */
  logProfileUpdated(): void {
    this.logEvent(ANALYTICS_EVENTS.PROFILE_UPDATED, {
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.PROFILE,
    });
  }

  /**
   * Log when security settings are updated.
   */
  logSecuritySettingsUpdated(): void {
    this.logEvent(ANALYTICS_EVENTS.SECURITY_SETTINGS_UPDATED, {
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.PROFILE,
    });
  }

  // ============================================================
  // Data Viewer Events
  // ============================================================

  /**
   * Log when data viewer is accessed.
   */
  logDataViewerAccessed(studyId: string): void {
    this.logEvent(ANALYTICS_EVENTS.DATA_VIEWER_ACCESSED, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.DATA_VIEWER,
    });
  }

  /**
   * Log when a specific night is viewed in the data viewer.
   */
  logNightViewed(studyId: string, nightNumber: number): void {
    this.logEvent(ANALYTICS_EVENTS.NIGHT_VIEWED, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.NIGHT_NUMBER]: nightNumber,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.DATA_VIEWER,
    });
  }

  // ============================================================
  // Navigation Events
  // ============================================================

  /**
   * Log navigation to a specific section.
   */
  logNavigation(destination: string): void {
    this.logEvent(ANALYTICS_EVENTS.NAVIGATION, {
      [ANALYTICS_PARAMS.DESTINATION]: destination,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.NAVIGATION,
    });
  }

  // ============================================================
  // Error Events
  // ============================================================

  /**
   * Log an error event for tracking failures.
   */
  logError(errorType: string, errorMessage?: string, feature?: AnalyticsFeature): void {
    this.logEvent(ANALYTICS_EVENTS.ERROR, {
      [ANALYTICS_PARAMS.ERROR_TYPE]: errorType,
      [ANALYTICS_PARAMS.ERROR_MESSAGE]: errorMessage,
      [ANALYTICS_PARAMS.FEATURE]: feature || AnalyticsFeature.GENERAL,
    });
  }

  // ============================================================
  // API Client Events
  // ============================================================

  /**
   * Log API client usage.
   */
  logApiClientUsed(endpoint: string): void {
    this.logEvent(ANALYTICS_EVENTS.API_CLIENT_USED, {
      [ANALYTICS_PARAMS.ENDPOINT]: endpoint,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.API_CLIENT,
    });
  }

  // ============================================================
  // Additional High-Value Events
  // ============================================================

  /**
   * Log when a user session expires.
   */
  logSessionExpired(): void {
    this.logEvent(ANALYTICS_EVENTS.SESSION_EXPIRED, {
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.AUTHENTICATION,
    });
  }

  /**
   * Log when a study is deleted.
   */
  logStudyDeleted(studyId: string): void {
    this.logEvent(ANALYTICS_EVENTS.STUDY_DELETED, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.STUDIES,
    });
  }

  /**
   * Log when a study is updated.
   */
  logStudyUpdated(studyId: string): void {
    this.logEvent(ANALYTICS_EVENTS.STUDY_UPDATED, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.STUDIES,
    });
  }

  /**
   * Log when a center is viewed.
   */
  logCenterViewed(centerId: string): void {
    this.logEvent(ANALYTICS_EVENTS.CENTER_VIEWED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  /**
   * Log when a center is updated.
   */
  logCenterUpdated(centerId: string): void {
    this.logEvent(ANALYTICS_EVENTS.CENTER_UPDATED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  /**
   * Log when a user role in a center is updated.
   */
  logCenterUserRoleUpdated(centerId: string, userRole: string): void {
    this.logEvent(ANALYTICS_EVENTS.CENTER_USER_ROLE_UPDATED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.USER_ROLE]: userRole,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  /**
   * Log when a user is removed from a center.
   */
  logCenterUserRemoved(centerId: string): void {
    this.logEvent(ANALYTICS_EVENTS.CENTER_USER_REMOVED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  /**
   * Log when user switches between centers.
   */
  logCenterSwitched(centerId: string): void {
    this.logEvent(ANALYTICS_EVENTS.CENTER_SWITCHED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  /**
   * Log when a snippet is created.
   */
  logSnippetCreated(centerId: string): void {
    this.logEvent(ANALYTICS_EVENTS.SNIPPET_CREATED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  /**
   * Log when a snippet is updated.
   */
  logSnippetUpdated(centerId: string): void {
    this.logEvent(ANALYTICS_EVENTS.SNIPPET_UPDATED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  /**
   * Log when a snippet is deleted.
   */
  logSnippetDeleted(centerId: string): void {
    this.logEvent(ANALYTICS_EVENTS.SNIPPET_DELETED, {
      [ANALYTICS_PARAMS.CENTER_ID]: centerId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.CENTERS,
    });
  }

  // ============================================================
  // Profile Events
  // ============================================================

  /**
   * Log when profile is viewed.
   */
  logProfileViewed(): void {
    this.logEvent(ANALYTICS_EVENTS.PROFILE_VIEWED, {
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.PROFILE,
    });
  }

  /**
   * Log when password is changed.
   */
  logPasswordChanged(): void {
    this.logEvent(ANALYTICS_EVENTS.PASSWORD_CHANGED, {
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.PROFILE,
    });
  }

  /**
   * Log when all nights view is accessed.
   */
  logAllNightsViewed(studyId: string): void {
    this.logEvent(ANALYTICS_EVENTS.ALL_NIGHTS_VIEWED, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.DATA_VIEWER,
    });
  }

  /**
   * Log when interpretation is viewed.
   */
  logInterpretationViewed(studyId: string): void {
    this.logEvent(ANALYTICS_EVENTS.INTERPRETATION_VIEWED, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.DATA_VIEWER,
    });
  }

  /**
   * Log when interpretation is saved.
   */
  logInterpretationSaved(studyId: string): void {
    this.logEvent(ANALYTICS_EVENTS.INTERPRETATION_SAVED, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.DATA_VIEWER,
    });
  }

  /**
   * Log when interpretation is signed and saved.
   */
  logInterpretationSigned(studyId: string): void {
    this.logEvent(ANALYTICS_EVENTS.INTERPRETATION_SIGNED, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.DATA_VIEWER,
    });
  }

  /**
   * Log when interpretation form is reset.
   */
  logInterpretationReset(studyId: string): void {
    this.logEvent(ANALYTICS_EVENTS.INTERPRETATION_RESET, {
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.DATA_VIEWER,
    });
  }

  /**
   * Log when report printing is initiated.
   */
  logReportPrinted(reportType: string, studyId?: string): void {
    this.logEvent(ANALYTICS_EVENTS.REPORT_PRINTED, {
      [ANALYTICS_PARAMS.REPORT_TYPE]: reportType,
      [ANALYTICS_PARAMS.STUDY_ID]: studyId,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.REPORTS,
    });
  }

  /**
   * Log when report generation fails.
   */
  logReportGenerationFailed(reportType: string, errorMessage?: string): void {
    this.logEvent(ANALYTICS_EVENTS.REPORT_GENERATION_FAILED, {
      [ANALYTICS_PARAMS.REPORT_TYPE]: reportType,
      [ANALYTICS_PARAMS.ERROR_MESSAGE]: errorMessage,
      [ANALYTICS_PARAMS.FEATURE]: AnalyticsFeature.REPORTS,
    });
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

export default analyticsService;
